import React, { useEffect, useState } from 'react';
import { useGame } from '../contexts/GameContext';
import { TomeChapter } from '../primitives/tome';
import { JournalDetail } from './Quest/JournalDetail';
import { JournalEmpty, JournalError, JournalLoading, JournalNearbyGivers } from './Quest/JournalEmpty';
import { journalEntries, type JournalQuest } from './Quest/questJournalModel';
import { focusJournalQuest, focusTrackerQuest, questJournalOwner, useQuestJournalFocus } from './Quest/questJournalFocus';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import { QuestPreviewCard } from './Quest/QuestPreviewCard';
import { previewHover } from './Quest/previewHover';
import { DiscoveredQuestsStore } from './Quest/DiscoveredQuestsStore';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { isKeyboardFocus } from '../components/useTouchHoldPin';
import { useCurrentMapId } from './WorldMap/WorldMapWindow';
import { nearbyQuestGivers } from '../../systems/npc/npcQuestOffers';
import './Quest/QuestJournal.css';

const STATUS: Record<JournalQuest['state'], string> = {
  ready: 'Ready to turn in', active: 'In progress', available: 'Ready to take up', locked: 'Locked chapter', completed: 'Completed',
};

/**
 * The quest journal on Tome parchment (Tome UI round 2): chapters for ready, active, ready-to-take-up,
 * discovered and completed quests beside a reading page. Projects incoming progress and completion IDs; it never
 * grants, accepts or completes anything. A page that fails to draw shows its own error page, not a blank HUD.
 */
export const QuestContent: React.FC = () =>
  <ErrorBoundary name="QuestJournal" fallback={retry => <JournalError onRetry={retry} />}><JournalBody /></ErrorBoundary>;

const JournalBody: React.FC = () => {
  const { player } = useGame();
  const mapId = useCurrentMapId();
  const [, setRefreshKey] = useState(0);
  const [hover, setHover] = useState<{ quest: JournalQuest; pos: { x: number; y: number } } | null>(null);

  useEffect(() => EventBus.getInstance().on(EVENTS.QUEST_DISCOVERED_CHANGED, () => setRefreshKey(k => k + 1)), []);

  const log = player?.quests ?? { active: [], completed: [] };
  const owner = questJournalOwner(player);
  const entries = journalEntries(log, DiscoveredQuestsStore.getDiscoveredIds(owner), player?.level);
  const ready = entries.active.filter(quest => quest.state === 'ready');
  const inProgress = entries.active.filter(quest => quest.state !== 'ready');
  const all = [...ready, ...inProgress, ...entries.available, ...entries.locked, ...entries.completed];
  const displayFocus = useQuestJournalFocus(owner, entries.active.map(quest => quest.id), all.map(quest => quest.id));
  const [selection, setSelection] = useState<{ owner: string; id: string | null }>({ owner, id: null });
  const selected = all.find(quest => quest.id === displayFocus.journalId)
    ?? all.find(quest => selection.owner === owner && quest.id === selection.id) ?? all[0];
  const select = (quest: JournalQuest) => {
    setSelection({ owner, id: quest.id });
    focusJournalQuest(owner, quest.id);
  };

  const row = (quest: JournalQuest) => <button
    type="button"
    key={quest.id}
    className="quest-journal-row"
    data-tone={quest.state}
    aria-label={`Open ${quest.title}${quest.title === 'Unknown quest' ? ` (${quest.id})` : ''}`}
    aria-pressed={selected?.id === quest.id}
    onClick={() => select(quest)}
    {...previewHover(pos => setHover({ quest, pos }), () => setHover(null))}
    onFocus={e => {
      if (!isKeyboardFocus(e.currentTarget)) return;
      const r = e.currentTarget.getBoundingClientRect();
      setHover({ quest, pos: { x: r.right, y: r.top } });
    }}
    onBlur={() => setHover(null)}
    onKeyDown={e => { if (e.key === 'Escape' && hover) { e.stopPropagation(); setHover(null); } }}
  >
    <strong>{quest.featured && <span aria-hidden className="quest-journal-mark">✦</span>}{quest.title}</strong>
    {quest.title === 'Unknown quest' && <small>{quest.id}</small>}
    <span data-journal-status={quest.state}>{STATUS[quest.state]}</span>
  </button>;

  const summary = [
    `${entries.active.length} active`,
    ...(ready.length ? [`${ready.length} ready`] : []),
    ...(entries.available.length ? [`${entries.available.length} to take up`] : []),
    ...(entries.locked.length ? [`${entries.locked.length} locked`] : []),
    `${entries.completed.length} completed`,
  ].join(' · ');

  // No quest log yet (the character is still loading): no chapters or blank page, which would claim it is empty.
  if (!player?.quests) return <div className="quest-journal"><div className="quest-journal-body">
    <nav className="quest-journal-rail" aria-label="Quest entries"><p className="quest-journal-summary">Loading…</p></nav>
    <JournalLoading />
  </div></div>;

  // Who on this map would offer a quest now, for the empty states (only when nothing else is under way).
  const givers = inProgress.length ? [] : nearbyQuestGivers(mapId, {
    level: player.level ?? 1, activeQuestIds: new Set(log.active.map(quest => quest.id)), completedQuestIds: new Set(log.completed),
  });

  return <div className="quest-journal">
    <div className="quest-journal-body">
      <nav className="quest-journal-rail" aria-label="Quest entries">
        <p className="quest-journal-summary">{summary}</p>
        {ready.length > 0 && <TomeChapter title="Ready to turn in" count={ready.length} tone="ready" aria-label={`Ready to turn in (${ready.length})`}>{ready.map(row)}</TomeChapter>}
        <TomeChapter title="Active quests" count={inProgress.length} tone="active" aria-label={`Active quests (${inProgress.length})`}>
          {inProgress.length ? inProgress.map(row) : <>
            <p className="quest-journal-empty">{ready.length ? 'Nothing else under way.' : 'No active quests.'}</p>
            <JournalNearbyGivers givers={givers} />
          </>}
        </TomeChapter>
        {entries.available.length > 0 && <TomeChapter title="Ready to take up" count={entries.available.length} tone="available" aria-label={`Ready to take up (${entries.available.length})`}>
          {entries.available.map(row)}</TomeChapter>}
        {entries.locked.length > 0 && <TomeChapter title="Discovered chapters" count={entries.locked.length} tone="locked" aria-label={`Discovered chapters (${entries.locked.length})`}>
          {entries.locked.map(row)}</TomeChapter>}
        {entries.completed.length > 0 && <TomeChapter title="Completed quests" count={entries.completed.length} tone="quiet" aria-label={`Completed quests (${entries.completed.length})`}>
          {entries.completed.map(row)}</TomeChapter>}
      </nav>
      {selected ? <JournalDetail quest={selected} completedIds={log.completed} playerClass={player?.class} focused={displayFocus.trackerId === selected.id}
        onFocus={owner ? () => focusTrackerQuest(owner, selected.id) : undefined}
        onClearFocus={owner ? () => focusTrackerQuest(owner, null) : undefined} />
        : <JournalEmpty givers={givers} />}
    </div>
    {hover && <QuestPreviewCard quest={hover.quest} completedIds={log.completed} pointerPos={hover.pos} />}
  </div>;
};
